import React from 'react';
// import ReactDOM from 'react-dom'; // For React 17
import { createRoot } from 'react-dom/client'; // For React 18
import { BrowserRouter as Router } from 'react-router-dom';
import './styles/styles.scss';
import App from './App/App';
import reportWebVitals from './reportWebVitals';
import { ThemeContextProvider } from './contexts/themeContext';
import { AuthContextProvider } from './contexts/authContext';
import { ChatUnreadProvider } from './contexts/chatUnreadContext';
import { OrganizationProvider } from './contexts/organizationContext';
import { FeatureProvider } from './contexts/featureContext';
import { EntitlementProvider } from './contexts/entitlementContext';
import { PermissionProvider } from './contexts/permissionContext';
// Side-effect import: initialises i18next with language detection,
// namespace loading, and fallback configuration.
// @userstory US-I18N-01 — Bootstraps translation system at application startup
import './i18n';

// ─────────────────────────────────────────────────────────────────────────────
// Build provenance — expose the deployed frontend version on `window.__APP_VERSION__`
// and log it once on startup. Useful when reading App Insights traces or when
// asking a user "which version are you on?" during support.
// `REACT_APP_VERSION` is set by the CalVer step in the deploy workflows.
// ─────────────────────────────────────────────────────────────────────────────
declare global {
	interface Window {
		__APP_VERSION__?: string;
	}
}
const APP_VERSION = process.env.REACT_APP_VERSION ?? 'dev';
window.__APP_VERSION__ = APP_VERSION;
// eslint-disable-next-line no-console
console.info(`[OpenTalents] frontend version: ${APP_VERSION}`);

// ─────────────────────────────────────────────────────────────────────────────
// i18n missing-key telemetry — forwards "missing translation" events emitted by
// src/i18n.ts to App Insights when the SDK is present on `window`. Decoupled by
// design: i18n.ts only dispatches a CustomEvent, this file owns the SDK hook.
// ─────────────────────────────────────────────────────────────────────────────
type AppInsightsLike = {
	trackEvent?: (event: { name: string; properties?: Record<string, string> }) => void;
};
window.addEventListener('i18n:missing-key', ((evt: CustomEvent) => {
	const { lngs, ns, key } = evt.detail as { lngs: string[]; ns: string; key: string };
	const ai =
		(window as unknown as { appInsights?: AppInsightsLike }).appInsights ??
		(window as unknown as { applicationInsights?: AppInsightsLike }).applicationInsights;
	ai?.trackEvent?.({
		name: 'I18nMissingKey',
		properties: {
			namespace: ns,
			key,
			languages: Array.isArray(lngs) ? lngs.join(',') : String(lngs ?? ''),
			appVersion: APP_VERSION,
		},
	});
}) as EventListener);

const children = (
	<Router>
		<ThemeContextProvider>
			<AuthContextProvider>
				<FeatureProvider>
					<EntitlementProvider>
						<PermissionProvider>
							<OrganizationProvider>
								<ChatUnreadProvider>
									<React.StrictMode>
										<App />
									</React.StrictMode>
								</ChatUnreadProvider>
							</OrganizationProvider>
						</PermissionProvider>
					</EntitlementProvider>
				</FeatureProvider>
			</AuthContextProvider>
		</ThemeContextProvider>
	</Router>
);

const container = document.getElementById('root');

// ReactDOM.render(children, container); // For React 17
createRoot(container as Element).render(children); // For React 18

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals();
